<template>
  <div>
    <div class="warranty-list-table">
      <b-table :items="items" :fields="computedFields" bordered hover :busy="isLoading" class="table-hover">
        <template v-slot:table-busy>
          <vcl-table :speed="5" :animate="true" :columns="10" />
        </template>
        <template v-if="selectable" #head(selected)>
          <b-form-checkbox :checked="isAllSelected" :indeterminate="isIndeterminate" @change="toggleAll" />
        </template>
        <template v-if="selectable" #cell(selected)="row">
          <b-form-checkbox :checked="selectedIds.includes(row.item.id)" @change="toggleRow(row.item.id)" />
        </template>
        <template #cell(id)="row">
          <div>
            <div class="text-primary font-weight-bold hover-link" @click="openModalWarrantyInfo(row.item)">
              {{ row.item.id }}
            </div>
            <div v-if="row.item.billNumber" class="small text-muted">
              Từ HĐ:
              <span class="text-primary font-weight-bold hover-link" @click="linkToBill(row.item.billNumber)"
                v-b-tooltip.hover.top title="Chi tiết hóa đơn">
                {{ row.item.billNumber }}
              </span>
            </div>
            <div v-if="row.item.tradeInId" class="small text-muted">
              Từ thu cũ:
              <span class="text-primary font-weight-bold hover-link" @click="linkToTradeIn(row.item.tradeInId)"
                v-b-tooltip.hover.top title="Chi tiết phiếu thu cũ">
                {{ row.item.tradeInId }}
              </span>
            </div>
          </div>
        </template>
        <template #cell(info)="row">
          <ul class="list-unstyled mb-1">
            <li>
              <span class="font-weight-bold">Ngày TN:</span>
              {{ row.item.receptionDate }}
            </li>
            <li>
              <span class="font-weight-bold">KTV kiểm tra: </span>
              <span class="text-primary font-weight-bold">{{
                row.item.inspectedByEmployeeName
                }}</span>
            </li>
            <li>
              <span class="font-weight-bold">Loại DV:</span>
              {{ row.item.warrantyServiceName }}
            </li>
            <li v-if="row.item.lendingStockSlip">
              <span class="font-weight-bold">Phiếu cho mượn:</span>
              <small class="hover-link text-primary font-weight-bold ml-1"
                @click.stop="openLendingStockSlip(row.item.lendingStockSlip)">
                {{ row.item.lendingStockSlip.code }}
              </small>
            </li>
            <li v-if="row.item.latestOutsource" class="hover-link" @click="$emit('view-outsource', row.item)">
              <span class="font-weight-bold">Outsource: </span>
              <OutsourceStatusBadge :status="row.item.latestOutsource.status"
                :status-name="row.item.latestOutsource.statusName" />
            </li>
            <li v-if="row.item.isCannotRepair">
              <b-badge variant="danger">Không sửa được</b-badge>
            </li>
            <li v-if="row.item.isDisagreePrice">
              <b-badge variant="warning">Không đồng ý giá</b-badge>
            </li>
          </ul>
        </template>
        <template #cell(customerPhone)="row">
          <span class="font-weight-bold hover-link" @click="linkToCustomer(row.item)">{{ row.item.customerName
            }}</span><br />
          <span @click="linkToCustomer(row.item)" class="hover-link">{{ row.item.customerPhone }}</span>
        </template>
        <template #cell(imeiCode)="row">
          <span v-b-tooltip.hover.top title="Tên sản phẩm">{{ row.item.productName }}</span>
          <CompanyCodeBadge class="ml-1" :company-code="row.item.companyCode" :company-name="row.item.companyName" /><br />
          <span class="text-primary font-italic small">IMEI: {{ row.item.imeiCode }}</span>
          <template v-if="row.item.warrantyProduct?.productName">
            <br />
            <span class="d-inline-flex align-items-center mt-1">
              <b-badge variant="success"
                class="warranty-list-package-badge d-inline-flex align-items-center justify-content-center mr-1"
                v-b-tooltip.hover.top :title="`Gói bảo hành: ${row.item.warrantyProduct.productName}`">
                <i class="fa fa-shield-alt"></i>
              </b-badge>

              <span class="warranty-product-name text-muted">
                {{ row.item.warrantyProduct.productName }}
              </span>
            </span>
          </template>
        </template>
        <template #cell(storeName)="row">
          <span class="d-inline-flex align-items-center flex-wrap">
            <span class="text-muted" v-if="row.item.previousStoreName">{{ row.item.previousStoreName }}</span>
            <i v-if="row.item.previousStoreName" class="fas fa-arrow-right text-primary mx-2" style="font-size: 16px"
              title="Chuyển sang"></i>
            <span class="font-weight-bold">{{ row.item.storeName }}</span>
          </span>
          <div v-if="row.item.transferStatus === WARRANTY_TRANSFER_STATUS.PENDING" class="mt-1">
            <b-badge variant="warning" class="mb-1" style="font-size: 10px; padding: 0.25em 0.4em">Chờ xác
              nhận</b-badge>
            <br />
            <b-button v-if="row.item.storeId === currentStoreId" size="sm" variant="outline-primary"
              class="py-0 px-2 mt-1" style="font-size: 11px" @click="$emit('confirm-transfer', row.item)">
              Xác nhận
            </b-button>
          </div>
        </template>
        <template #cell(statusName)="row">
          <WarrantyStatusBadge :status="row.item.status" :status-name="row.item.statusName"
            :class="{ 'hover-link': canEditStatus }" @click.native="canEditStatus && onOpenChangeStatus(row.item)" />
          <br />
          <span class="text-muted small mt-1" v-b-tooltip.hover.top title="Ngày đổi trạng thái">{{
            row.item.statusChangeDate }}</span>
        </template>
        <template #cell(paymentStatus)="row">
          <div class="text-left text-price">
            <p class="d-block mb-1 text-right">
              Báo giá:
              <span class="font-weight-bold">{{ convertPrice(row.item.estimatedCost) }}</span>
            </p>
            <p class="d-block mb-1 text-right">
              Tiền thu khách:
              <span class="font-weight-bold">{{ convertPrice(row.item.actualCost) }}</span>
            </p>
            <p v-if="
              row.item.actualCost != null && row.item.diffEstimate
            " class="d-block mb-1 text-right text-danger">
              <span v-b-tooltip.hover.top
                title="Chênh lệch tiền thu khách so với báo giá(Tiền thu khách - Báo giá - Phí hỗ trợ bảo hành)">Chênh
                lệch: {{ convertPrice(Math.abs(row.item.diffEstimate)) }}</span>
            </p>
            <hr class="my-2" v-if="row.item.payments.length" />
            <template v-if="row.item.payments && row.item.payments.length">
              <div v-for="(payment, idx) in row.item.payments" :key="idx" class="mb-1 text-right">
                <i v-b-tooltip.hover :title="payment.paymentName" class="mr-1" :class="payment.paymentType === PAYMENT_TYPE.CASH
                    ? 'fas fa-money-bill-alt'
                    : payment.paymentType === PAYMENT_TYPE.TRANSFER
                      ? 'fa fa-university'
                      : payment.paymentType === PAYMENT_TYPE.CREDIT
                        ? 'fa fa-credit-card'
                        : payment.paymentType === PAYMENT_TYPE.INSTALLMENT
                          ? 'fa fa-cube'
                          : payment.paymentType === PAYMENT_TYPE.DEPOSIT
                            ? 'fas fa-hand-holding-usd'
                            : ''
                  "></i>
                <span class="font-weight-bold">{{ convertPrice(payment.paymentAmount || 0) }}</span>
              </div>
            </template>
            <p v-if="row.item.extraAmount > 0" class="d-block mb-0 text-right text-primary">
              Tiền thừa: <span class="font-weight-bold">{{ convertPrice(row.item.extraAmount) }}</span>
            </p>
            <p v-if="row.item.extraAmount < 0" class="d-block mb-0 mt-1 text-right" style="color: #e65b5b">
              Còn thiếu: <span class="font-weight-bold">{{ convertPrice((row.item.extraAmount || 0) * -1) }}</span>
            </p>
            <p v-if="row.item.leftAmount > 0" class="d-block mb-0 mt-1 text-right">
              Công nợ: <span class="font-weight-bold">{{ convertPrice(row.item.leftAmount) }}</span>
            </p>
            <span v-if="
              (!row.item.payments || !row.item.payments.length) &&
              row.item.extraAmount != null &&
              row.item.extraAmount === 0 &&
              (row.item.leftAmount == null || row.item.leftAmount === 0)
            " class="text-muted"></span>
          </div>
        </template>
        <template #cell(note)="row">
          <p v-if="row.item.issueDescription" v-b-tooltip.hover :title="row.item.issueDescription">
            <strong>Mô tả lỗi:</strong> &nbsp;{{ truncateText(row.item.issueDescription, 30) }}
          </p>
          <p v-if="row.item.internalNote" v-b-tooltip.hover :title="row.item.internalNote"
            class="internal-note-row mb-1">
            <strong>Ghi chú nội bộ:</strong> &nbsp;{{ truncateText(row.item.internalNote, 30) }}
            <i v-if="canEdit" class="fas fa-pen ml-1 text-primary hover-link edit-note-icon" style="font-size: 10px"
              @click.stop="openEditInternalNote(row.item)" v-b-tooltip.hover title="Sửa ghi chú nội bộ"></i>
          </p>
          <p v-else-if="canEdit" class="internal-note-row mb-1">
            <span class="text-muted">Ghi chú nội bộ:</span>
            <i class="fas fa-pen ml-1 text-primary hover-link edit-note-icon" style="font-size: 10px"
              @click.stop="openEditInternalNote(row.item)" v-b-tooltip.hover title="Thêm ghi chú nội bộ"></i>
          </p>
          <p v-if="row.item.externalNote" v-b-tooltip.hover :title="row.item.externalNote">
            <strong>Ngoại quan:</strong> &nbsp;{{ truncateText(row.item.externalNote, 30) }}
          </p>
          <p v-if="row.item.rejectReason" v-b-tooltip.hover :title="row.item.rejectReason">
            <strong>Lý do từ chối:</strong> &nbsp;{{ truncateText(row.item.rejectReason, 30) }}
          </p>
        </template>
        <template #cell(actions)="row">
          <div class="d-flex justify-content-center">
            <b-dropdown size="sm" id="dropdown-left" no-caret right>
              <template slot="button-content">
                <i class="flaticon2-settings"></i>
              </template>
              <b-dropdown-item v-if="canPrint" @click="$emit('print', row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="flaticon2-printer"></i>
                  &nbsp; In phiếu
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="canEdit && !isViewOnlyStatus(row.item)" @click="$emit('edit', row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="flaticon2-pen"></i>
                  &nbsp; Chỉnh sửa
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="canEdit && isViewOnlyStatus(row.item)" @click="$emit('view-detail', row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="fas fa-eye"></i>
                  &nbsp; Chi tiết
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="canEditStatus" @click="onOpenChangeStatus(row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="fas fa-sync-alt icon-color"></i>
                  &nbsp; Đổi trạng thái
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="canComplete && isShowCompleteButton(row.item)"
                @click="onCompleteWarranty(row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="fas fa-check-circle"></i>
                  &nbsp; Hoàn thành phiếu
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="canReturn && isShowReturnButton(row.item)" @click="onReturnWarranty(row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="fas fa-undo-alt"></i>
                  &nbsp; Trả bảo hành
                </span>
              </b-dropdown-item>

              <b-dropdown-item v-if="canChangeStore && !isReturnedStatus(row.item)"
                @click="$emit('change-store', row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="fas fa-store"></i>
                  &nbsp; Chuyển cửa hàng
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="canCreateLendingStockSlip && !isViewOnlyStatus(row.item)"
                @click="$emit('create-lending-stock-slip', row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="fas fa-toolbox"></i>
                  &nbsp; Tạo phiếu cho mượn máy
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="canCreateOutsource && isShowCreateOutsourceButton(row.item)"
                @click="$emit('create-outsource', row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="fas fa-truck-loading"></i>
                  &nbsp; Chuyển Outsource
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="row.item.latestOutsource && row.item.latestOutsource.status === 'SENT'"
                @click="$emit('view-outsource', row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem" class="fas fa-external-link-alt"></i>
                  &nbsp; Xem outsource
                </span>
              </b-dropdown-item>
              <b-dropdown-item v-if="canDelete && isShowDeleteButton(row.item)" @click="$emit('delete', row.item)">
                <span style="color: #3f4254; font-size: 12px">
                  <i style="font-size: 1rem; color: #d33" class="flaticon2-rubbish-bin-delete-button"></i>
                  &nbsp; Xóa
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </b-table>
      <b-row>
        <b-col>
          <p class="mt-3 text-dark" style="font-weight: 500">
            Hiển thị {{ fromNo }} - {{ toNo }} / {{ convertPrice(totalItems) }} bản ghi
          </p>
        </b-col>
        <b-col>
          <BPaginationNavCustom :number-of-pages="numberOfPage || 1" :current-page="currentPage"
            :total-page="numberOfPage" :link-params="paginationLinkParams"
            @page-change="$emit('page-change', $event)" />
        </b-col>
      </b-row>
      <!-- End of Paginate session -->
    </div>
    <b-modal id="modal-warranty-info" ref="modalWarrantyInfo" size="xl" @hidden="warrantyId = null">
      <WarrantyInfo v-if="detailMode === 'dispatch' && warrantyId" :warranty-id="warrantyId"
        @close="closeModalWarrantyInfo" />
      <WarrantyReceptionInfo v-else-if="detailMode === 'reception' && warrantyId" :warranty-id="warrantyId"
        @close="closeModalWarrantyInfo" />
      <template #modal-footer>
        <b-button size="sm" variant="secondary" @click="closeModalWarrantyInfo">
          Đóng
        </b-button>
      </template>
    </b-modal>

    <ModalConfirmCompleteWarranty ref="modalConfirmComplete" :warranty="selectedWarrantyToComplete"
      @confirm="onConfirmCompleteWarranty" />

    <b-modal id="modal-edit-internal-note" ref="modalEditInternalNote" title="Sửa ghi chú nội bộ"
      @hidden="resetEditInternalNote">
      <b-form-group label="Ghi chú nội bộ">
        <b-form-textarea v-model="editingInternalNote" rows="4" placeholder="Nhập ghi chú nội bộ..." />
      </b-form-group>
      <template #modal-footer>
        <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-edit-internal-note')">
          Hủy
        </b-button>
        <b-button size="sm" variant="primary" :disabled="isSavingInternalNote" @click="saveInternalNote">
          <b-spinner v-if="isSavingInternalNote" small class="mr-1" />
          Lưu
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { VclTable } from 'vue-content-loading'
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue'
import { convertPrice, truncateText, getCompanyCodeColor } from '@/utils/common'
import { PAYMENT_TYPE, WARRANTY_TRANSFER_STATUS } from '@/utils/enum'
import WarrantyStatusBadge from './WarrantyStatusBadge.vue'
import WarrantyInfo from '../WarrantyInfo.vue'
import WarrantyReceptionInfo from '../WarrantyReceptionInfo.vue'
import { WARRANTY_RECEPTION_STATUS } from '@/utils/enum'
import { showConfirmNotify } from '@/utils/sweet-alert2'
import { updateInternalNote } from '../utils/warrantyApi'
import OutsourceStatusBadge from '../outsource/components/OutsourceStatusBadge.vue'
import ModalConfirmCompleteWarranty from './ModalConfirmCompleteWarranty.vue'
import CompanyCodeBadge from '@/view/components/common/CompanyCodeBadge.vue'
export default {
  name: 'WarrantyListTable',
  components: {
    VclTable,
    BPaginationNavCustom,
    WarrantyStatusBadge,
    WarrantyInfo,
    WarrantyReceptionInfo,
    OutsourceStatusBadge,
    ModalConfirmCompleteWarranty,
    CompanyCodeBadge
  },
  props: {
    detailMode: {
      type: String,
      default: 'dispatch',
      validator: (v) => ['dispatch', 'reception'].includes(v),
    },
    items: {
      type: Array,
      default: () => [],
    },
    fields: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    numberOfPage: {
      type: Number,
      default: 1,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    limit: {
      type: Number,
      default: 10,
    },
    paginationLinkParams: {
      type: Object,
      default: () => ({}),
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    canPrint: {
      type: Boolean,
      default: false,
    },
    selectable: {
      type: Boolean,
      default: false,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    canCreateLendingStockSlip: {
      type: Boolean,
      default: false,
    },
    canCreateOutsource: {
      type: Boolean,
      default: false,
    },
    canEditStatus: {
      type: Boolean,
      default: false,
    },
    canComplete: {
      type: Boolean,
      default: false,
    },
    canChangeStore: {
      type: Boolean,
      default: false,
    },
    currentStoreId: {
      type: Number,
      default: null,
    },
    canReturn: {
      type: Boolean,
      default: false,
    },
    canUpdateUnlimit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      PAYMENT_TYPE,
      WARRANTY_TRANSFER_STATUS,
      warrantyId: null,
      editingWarrantyItem: null,
      editingInternalNote: '',
      isSavingInternalNote: false,
      selectedWarrantyToComplete: {},
    }
  },
  computed: {
    computedFields() {
      if (!this.selectable) return this.fields
      const selectCol = {
        key: 'selected',
        label: '',
        thClass: 'text-center',
        tdClass: 'text-center',
        thStyle: { width: '40px' },
      }
      return [selectCol, ...this.fields]
    },
    isAllSelected() {
      if (!this.items.length) return false
      return this.items.every((i) => this.selectedIds.includes(i.id))
    },
    isIndeterminate() {
      const n = this.selectedIds.length
      return n > 0 && n < this.items.length
    },
    fromNo() {
      return (this.currentPage - 1) * this.limit + 1
    },
    toNo() {
      return this.currentPage * this.limit
    },
  },
  methods: {
    truncateText,
    getCompanyCodeColor,
    isViewOnlyStatus(item) {
      if (this.canUpdateUnlimit) return false
      return [WARRANTY_RECEPTION_STATUS.COMPLETED, WARRANTY_RECEPTION_STATUS.RETURNED].includes(
        item.status,
      )
    },
    isShowDeleteButton(item) {
      return [WARRANTY_RECEPTION_STATUS.RECEIVING, WARRANTY_RECEPTION_STATUS.REJECTED].includes(
        item.status,
      )
    },
    isShowCompleteButton(item) {
      return (
        item.status !== WARRANTY_RECEPTION_STATUS.COMPLETED &&
        item.status !== WARRANTY_RECEPTION_STATUS.RETURNED
      )
    },
    isShowReturnButton(item) {
      return item.status === WARRANTY_RECEPTION_STATUS.COMPLETED
    },
    isShowCreateOutsourceButton(item) {
      if (
        [WARRANTY_RECEPTION_STATUS.COMPLETED, WARRANTY_RECEPTION_STATUS.RETURNED].includes(
          item.status,
        )
      ) {
        return false
      }
      return !item.latestOutsource || item.latestOutsource.status !== 'SENT'
    },
    isReturnedStatus(item) {
      return item.status === WARRANTY_RECEPTION_STATUS.RETURNED
    },
    async onCompleteWarranty(item) {
      if (!item || !item.id) return

      this.selectedWarrantyToComplete = item
      this.$nextTick(() => {
        this.$refs.modalConfirmComplete.show()
      })
    },
    onConfirmCompleteWarranty(item) {
      this.$emit('complete-warranty', item)
    },
    async onReturnWarranty(item) {
      if (!item || !item.id) return

      const { isConfirmed } = await showConfirmNotify({
        title: 'Xác nhận trả bảo hành',
        text: 'Bạn có chắc muốn trả bảo hành cho khách hàng? Lưu ý thao tác này không thể hoàn tác!',
        icon: 'question',
        actionName: 'Trả bảo hành',
        confirmButtonColor: '#28a745',
      })
      if (!isConfirmed) return

      this.$emit('return-warranty', item)
    },
    toggleRow(id) {
      const next = this.selectedIds.includes(id)
        ? this.selectedIds.filter((x) => x !== id)
        : [...this.selectedIds, id]
      this.$emit('selection-change', next)
    },
    toggleAll() {
      const next = this.isAllSelected ? [] : this.items.map((i) => i.id)
      this.$emit('selection-change', next)
    },
    onOpenChangeStatus(item) {
      this.$emit('selection-change', [item.id])
      this.$emit('open-change-status-modal')
    },
    linkToBill(billNumber) {
      this.$router.push({
        name: 'update-bill',
        query: { id: billNumber },
      })
    },
    linkToTradeIn(tradeInId) {
      if (!tradeInId) return
      const routeData = this.$router.resolve({
        name: 'update-trade-in',
        query: { id: tradeInId },
      })
      window.open(routeData.href, '_blank')
    },
    linkToCustomer({ customerId, customerPhone }) {
      this.$router.push({
        name: 'update-customer',
        query: { id: customerId, phoneNo: customerPhone },
      })
    },
    convertPrice,
    openLendingStockSlip(lendingStockSlip) {
      if (!lendingStockSlip || !lendingStockSlip.id) return
      const routeData = this.$router.resolve({
        name: 'edit-lending-stockslip',
        query: { id: lendingStockSlip.id },
      })
      window.open(routeData.href, '_blank')
    },
    openModalWarrantyInfo(item) {
      if (!item?.id) return
      this.warrantyId = item.id
      this.$nextTick(() => {
        this.$bvModal.show('modal-warranty-info')
      })
    },
    closeModalWarrantyInfo() {
      this.$bvModal.hide('modal-warranty-info')
      this.warrantyId = null
    },
    openEditInternalNote(item) {
      this.editingWarrantyItem = item
      this.editingInternalNote = item.internalNote || ''
      this.$bvModal.show('modal-edit-internal-note')
    },
    resetEditInternalNote() {
      this.editingWarrantyItem = null
      this.editingInternalNote = ''
      this.isSavingInternalNote = false
    },
    async saveInternalNote() {
      if (!this.editingWarrantyItem) return
      this.isSavingInternalNote = true
      try {
        await updateInternalNote(this.editingWarrantyItem.id, this.editingInternalNote)
        this.editingWarrantyItem.internalNote = this.editingInternalNote
        this.$bvModal.hide('modal-edit-internal-note')
        this.$emit('refresh')
      } catch (err) {
        // handled by API
      } finally {
        this.isSavingInternalNote = false
      }
    },
  },
}
</script>

<style scoped>
/* Fix column widths: thStyle width % only works with table-layout: fixed */
.warranty-list-table>>>.table {
  width: 100%;
  table-layout: fixed;
}

/* b-badge là component con: scoped .class không ăn đủ; dùng >>> từ wrapper bảng.
   Icon FA nằm trong <i>, phải set font-size trên .fa chứ không chỉ trên .badge */
.warranty-list-table>>>.warranty-list-package-badge.badge {
  padding: 1px 4px !important;
  line-height: 1 !important;
  min-height: 0 !important;
  vertical-align: middle;
}

.warranty-list-table>>>.warranty-list-package-badge.badge .fa {
  font-size: 10px !important;
  line-height: 1 !important;
  vertical-align: middle;
}

.warranty-product-name {
  font-size: 10px;
}

.warranty-list-table>>>.table th,
.warranty-list-table>>>.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #ecf0f3;
}

.warranty-list-table>>>.table.b-table.table-sm>thead>tr>[aria-sort]:not(.b-table-sort-icon-left),
.warranty-list-table>>>.table.b-table.table-sm>tfoot>tr>[aria-sort]:not(.b-table-sort-icon-left) {
  background-position: right calc(1.5rem / 2) center;
  padding-right: calc(0.3rem + 0.65em);
}

.hover-link:hover {
  cursor: pointer;
  opacity: 0.6;
}

.internal-note-row {
  position: relative;
}

.internal-note-row .edit-note-icon {
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}

.internal-note-row:hover .edit-note-icon {
  opacity: 1;
}

.list-unstyled li {
  margin-bottom: 5px;
}

.text-price {
  font-size: 12px;
}
</style>
